// src/pages/auth/OAuthConsentPage.tsx
// OAuth permission grant screen.

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuthn, useAuth, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, CardFooter, Alert, ErrorAlert } from '@react/components';
import { AuthLayout } from './AuthLayout';

export const OAuthConsentPage = observer(() => {
  const authnStore = useAuthn();
  const auth = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();

  const clientId = searchParams.get('client_id') ?? '';
  const scopes = (searchParams.get('scope') ?? '').split(' ').filter(Boolean);
  const redirectUri = searchParams.get('redirect_uri') ?? '';
  const state = searchParams.get('state') ?? '';

  const [client, setClient] = useState<{ client_name: string; logo_uri: string; description: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [existingConsent, setExistingConsent] = useState<{ scopes: string[] } | null>(null);
  const [checkingConsent, setCheckingConsent] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    authnStore.GetOAuth2Client(clientId)
      .then((resp) => {
        const c = resp.client;
        setClient({ client_name: c?.client_name ?? '', logo_uri: c?.logo_uri ?? '', description: c?.description ?? '' });
      })
      .catch((err) => setError(err));
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for a previously granted consent so a returning user isn't re-prompted
  // for access they already gave — standard OIDC provider behavior.
  useEffect(() => {
    if (!clientId) return;
    setCheckingConsent(true);
    authnStore.GetOAuth2Consent(clientId)
      .then((resp) => {
        const consent = resp.consent;
        if (consent && !consent.revoked) {
          const granted = consent.granted_scopes?.items ?? [];
          const stillSufficient = scopes.every((s) => granted.includes(s));
          if (stillSufficient) setExistingConsent({ scopes: granted });
        }
      })
      .catch(() => { /* no prior consent — proceed with the normal prompt */ })
      .finally(() => setCheckingConsent(false));
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async () => {
    try {
      await authnStore.GrantOAuth2Consent({
        entity: {} as never,
        client_id: clientId,
        scopes: { items: scopes },
        expires_in: 0,
      });
      const params = new URLSearchParams({ state });
      window.location.href = `${redirectUri}?${params}`;
    } catch (err) {
      setError(err);
    }
  };

  const handleDeny = async () => {
    try {
      await authnStore.RevokeOAuth2Consent(clientId);
    } catch {
      // ignore
    }
    const params = new URLSearchParams({ error: 'access_denied', state });
    window.location.href = `${redirectUri}?${params}`;
  };

  // Already authorized with sufficient scopes on a prior visit — skip straight
  // through without asking again, matching standard OIDC provider behavior.
  const handleContinue = () => {
    const params = new URLSearchParams({ state });
    window.location.href = `${redirectUri}?${params}`;
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle as="h1" className="text-xl">{t('auth.oauthConsentTitle')}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {error ? <ErrorAlert error={error} /> : null}

          {client && (
            <>
              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                {client.logo_uri && <img src={client.logo_uri} alt="" className="w-10 h-10 rounded-lg" />}
                <div>
                  <p className="font-medium text-foreground">{client.client_name}</p>
                  {client.description && <p className="text-xs text-muted-foreground">{client.description}</p>}
                </div>
              </div>

              {existingConsent && (
                <Alert variant="info">{t('auth.oauthAlreadyAuthorized')}</Alert>
              )}

              <div>
                <p className="text-sm text-muted-foreground mb-2">{t('auth.oauthRequestsAccess')}</p>
                <ul className="space-y-2">
                  {scopes.map((scope) => (
                    <li key={scope} className="flex items-center gap-2 text-sm">
                      <svg className="w-4 h-4 text-primary shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-foreground">{scope}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">{t('auth.oauthSignedInAs')}</p>
                <p className="text-sm font-medium text-foreground">{auth.user?.email}</p>
              </div>
            </>
          )}
        </CardContent>

        <CardFooter className="flex gap-3">
          {existingConsent ? (
            <>
              <Button variant="secondary" fullWidth onClick={() => setExistingConsent(null)}>
                {t('auth.oauthChooseAgain')}
              </Button>
              <Button fullWidth onClick={handleContinue}>
                {t('common.continue')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" fullWidth onClick={handleDeny} disabled={checkingConsent}>
                {t('auth.deny')}
              </Button>
              <Button fullWidth onClick={handleApprove} isLoading={authnStore.isLoading} disabled={checkingConsent}>
                {t('auth.approve')}
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
    </AuthLayout>
  );
});

export default OAuthConsentPage;
